// Pure (DOM-free at import time) helpers for the Integration widget: parsing the
// server-rendered data island and the iframe-height postMessage contract with the
// AFAS/InSite/Simplr host. Kept out of the `@customElement` component module so the
// logic stays unit-testable under the Node test runner (no DOM harness).

export interface IntegrationItem {
  title: string;
  url: string;
}

export interface IntegrationCategory {
  key: string;
  label: string;
  iconUrl: string;
  items: IntegrationItem[];
}

export interface IntegrationInitialState {
  emptyText: string;
  categories: IntegrationCategory[];
}

/**
 * Parses the `#integration-initial-state` JSON island into a sanitized shape.
 * Defends the rendering boundary: malformed or partial islands degrade to empty
 * rather than throwing, and every field is coerced to its expected type.
 */
export function parseIntegrationInitialState(
  json: string,
): IntegrationInitialState {
  let parsed: Partial<IntegrationInitialState>;
  try {
    parsed = JSON.parse(json) as Partial<IntegrationInitialState>;
  } catch {
    return { emptyText: "", categories: [] };
  }

  const emptyText =
    typeof parsed.emptyText === "string" ? parsed.emptyText : "";
  const categories = Array.isArray(parsed.categories)
    ? parsed.categories
        .filter(
          (category): category is IntegrationCategory =>
            !!category && typeof category.key === "string",
        )
        .map((category) => ({
          key: category.key,
          label: typeof category.label === "string" ? category.label : "",
          iconUrl: typeof category.iconUrl === "string" ? category.iconUrl : "",
          items: Array.isArray(category.items)
            ? category.items
                .filter(
                  (item): item is IntegrationItem =>
                    !!item && typeof item.url === "string",
                )
                .map((item) => ({
                  title: typeof item.title === "string" ? item.title : "",
                  url: item.url,
                }))
            : [],
        }))
    : [];

  return { emptyText, categories };
}

export interface HeightRequest {
  anta: true;
  action: string;
  height?: number;
  [key: string]: unknown;
}

/**
 * Parses an incoming postMessage payload into an AFAS height request, or null when
 * it is not one. Mirrors the legacy `External/integration.js` guard (`request.anta
 * && request.action`) without throwing on non-JSON messages.
 */
export function parseHeightRequest(data: unknown): HeightRequest | null {
  if (typeof data !== "string") {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return null;
  }
  if (
    typeof parsed === "object" &&
    parsed !== null &&
    (parsed as { anta?: unknown }).anta === true &&
    typeof (parsed as { action?: unknown }).action === "string"
  ) {
    return parsed as HeightRequest;
  }
  return null;
}

/** Builds the push notification the legacy `Integration.js` posted to the parent. */
export function buildSetHeightMessage(height: number): string {
  return JSON.stringify({ anta: true, action: "setHeight", height });
}

/** Builds the reply to a parent `getHeight` request (echoes the request + height). */
export function buildGetHeightResponse(
  request: HeightRequest,
  height: number,
): string {
  return JSON.stringify({ ...request, height });
}

/**
 * Reports the widget's content height to the embedding AFAS/InSite host, preserving
 * both legacy behaviors with unchanged message shapes:
 *  - push `{ anta, action: "setHeight", height }` on start + on every resize
 *    (replaces `wwwroot/js/Integration.js`), and
 *  - answer parent `{ anta, action: "getHeight" }` pulls (replaces
 *    `wwwroot/js/External/integration.js`).
 * Touches `window`/`document` only when started, so importing this module is DOM-free.
 */
export class IframeHeightReporter {
  private resizeObserver: ResizeObserver | null = null;
  private readonly handleMessageBound = (event: MessageEvent): void =>
    this.handleMessage(event);

  start(): void {
    window.addEventListener("message", this.handleMessageBound);
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.notifyHeight());
      this.resizeObserver.observe(document.body);
    }
    this.notifyHeight();
  }

  stop(): void {
    window.removeEventListener("message", this.handleMessageBound);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  notifyHeight(): void {
    if (window.parent === window) {
      return;
    }
    window.parent.postMessage(buildSetHeightMessage(this.measureHeight()), "*");
  }

  private handleMessage(event: MessageEvent): void {
    const request = parseHeightRequest(event.data);
    if (!request || request.action !== "getHeight") {
      return;
    }
    const source = event.source as Window | null;
    source?.postMessage(
      buildGetHeightResponse(request, this.measureHeight()),
      event.origin,
    );
  }

  private measureHeight(): number {
    const body = document.body;
    const styles = getComputedStyle(body);
    let maxBottom = 0;
    for (const child of Array.from(body.children)) {
      const rect = child.getBoundingClientRect();
      if (rect.height > 0 && rect.bottom > maxBottom) {
        maxBottom = rect.bottom;
      }
    }
    const bodyTop = body.getBoundingClientRect().top;
    return (
      Math.ceil(maxBottom - bodyTop) +
      Number.parseInt(styles.paddingBottom || "0", 10)
    );
  }
}
